// src/pages/AccountsPage.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useBudget } from '../context/BudgetContext';
import './AccountsPage.css'; // Import the CSS file

const AccountsPage = () => {
  const { state, actions, formatCurrency } = useBudget();

  const emptyForm = {
    name: '',
    bank: '',
    accountNumber: '',
    transitNumber: '',
    branchNumber: '',
    currentBalance: ''
  };

  // Top form is for adding a new account.
  const [form, setForm] = useState(emptyForm);
  // Editing happens in a modal; `editing` holds the account being edited (or null).
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const accounts = Array.isArray(state.data.accounts) ? state.data.accounts : [];

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setEditField = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));

  const reset = () => setForm(emptyForm);

  // Build the persisted account object from form values. Keeps the legacy
  // `balance` field in sync so re-exported files remain consistent for both
  // the current and older account schemas.
  const buildAccountData = (values, id) => {
    const balance = parseFloat(values.currentBalance) || 0;
    return {
      ...values,
      name: values.name.trim(),
      bank: values.bank.trim(),
      currentBalance: balance,
      balance,
      id
    };
  };

  // Add a new account from the top form.
  const save = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Account name is required');
      return;
    }
    actions.addAccount(buildAccountData(form, `account-${Date.now()}`));
    reset();
  };

  // Open the edit modal, pre-filled with the account's current values.
  const openEdit = (acc) => {
    setEditForm({
      name: acc.name || '',
      bank: acc.bank || '',
      accountNumber: acc.accountNumber || '',
      transitNumber: acc.transitNumber || '',
      branchNumber: acc.branchNumber || '',
      currentBalance: String(acc.currentBalance ?? acc.balance ?? '')
    });
    setEditing(acc);
  };

  const closeEdit = () => setEditing(null);

  // Save changes made in the edit modal.
  const saveEdit = (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      alert('Account name is required');
      return;
    }
    actions.updateAccount(buildAccountData(editForm, editing.id));
    setEditing(null);
  };

  const deleteAccount = (accountId) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      actions.removeAccount(accountId);
    }
  };

  // Allow closing the modal with the Escape key.
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeEdit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  const total = accounts.reduce((s, a) => s + (parseFloat(a.currentBalance ?? a.balance) || 0), 0);

  // ----- Account-to-account transfers -----
  const emptyTransfer = {
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    note: ''
  };
  const [transferForm, setTransferForm] = useState(emptyTransfer);
  const setTransferField = (k, v) => setTransferForm(prev => ({ ...prev, [k]: v }));

  const transfers = Array.isArray(state.data.transfers) ? state.data.transfers : [];
  const accountLabel = (id) => {
    const acc = accounts.find(a => a.id === id);
    return acc ? acc.name : 'Unknown account';
  };

  const submitTransfer = (e) => {
    e.preventDefault();
    const amount = parseFloat(transferForm.amount);

    if (!transferForm.fromAccountId || !transferForm.toAccountId) {
      alert('Please choose both a source and a destination account.');
      return;
    }
    if (transferForm.fromAccountId === transferForm.toAccountId) {
      alert('The "From" and "To" accounts must be different.');
      return;
    }
    if (!(amount > 0)) {
      alert('Please enter a transfer amount greater than 0.');
      return;
    }
    const fromExists = accounts.some(a => a.id === transferForm.fromAccountId);
    const toExists = accounts.some(a => a.id === transferForm.toAccountId);
    if (!fromExists || !toExists) {
      alert('Both accounts must exist.');
      return;
    }

    actions.addTransfer({ ...transferForm, amount });
    // Keep the chosen date for convenience when logging several transfers.
    setTransferForm({ ...emptyTransfer, date: transferForm.date });
  };

  const undoTransfer = (id) => {
    if (window.confirm('Undo this transfer? The account balances will be reversed.')) {
      actions.deleteTransfer(id);
    }
  };

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <h1 className="accounts-title">🏦 Accounts</h1>
        <p className="accounts-description">
          Central place to add and edit accounts used throughout the app.
        </p>
      </div>

      <form onSubmit={save} className="account-form">
        <h2 className="account-form-title">➕ Add New Account</h2>
        <div className="form-grid">
          <input 
            className="form-input"
            placeholder="Account Name *" 
            value={form.name} 
            onChange={e => setField('name', e.target.value)} 
          />
          <input 
            className="form-input"
            placeholder="Bank" 
            value={form.bank} 
            onChange={e => setField('bank', e.target.value)} 
          />
          <input 
            className="form-input"
            placeholder="Account Number" 
            value={form.accountNumber} 
            onChange={e => setField('accountNumber', e.target.value)} 
          />
          <input 
            className="form-input"
            placeholder="Transit Number" 
            value={form.transitNumber} 
            onChange={e => setField('transitNumber', e.target.value)} 
          />
          <input 
            className="form-input"
            placeholder="Branch Number" 
            value={form.branchNumber} 
            onChange={e => setField('branchNumber', e.target.value)} 
          />
          <input 
            className="form-input"
            placeholder="Current Balance" 
            type="number"
            step="0.01"
            value={form.currentBalance} 
            onChange={e => setField('currentBalance', e.target.value)} 
          />
        </div>
        
        <div className="form-actions">
          <button type="button" onClick={reset} className="btn btn-reset">
            Reset
          </button>
          <button type="submit" className="btn btn-primary">
            Save Account
          </button>
        </div>
      </form>

      {/* Transfer Money between accounts */}
      <form onSubmit={submitTransfer} className="account-form transfer-form">
        <h2 className="account-form-title">🔁 Transfer Money</h2>
        {accounts.length < 2 ? (
          <p className="transfer-hint">
            Add at least two accounts above to move money between them.
          </p>
        ) : (
          <>
            <div className="form-grid">
              <label className="transfer-field">
                <span className="transfer-label">From Account *</span>
                <select
                  className="form-input"
                  value={transferForm.fromAccountId}
                  onChange={e => setTransferField('fromAccountId', e.target.value)}
                >
                  <option value="">Select source…</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </label>

              <label className="transfer-field">
                <span className="transfer-label">To Account *</span>
                <select
                  className="form-input"
                  value={transferForm.toAccountId}
                  onChange={e => setTransferField('toAccountId', e.target.value)}
                >
                  <option value="">Select destination…</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </label>

              <label className="transfer-field">
                <span className="transfer-label">Amount *</span>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={transferForm.amount}
                  onChange={e => setTransferField('amount', e.target.value)}
                />
              </label>

              <label className="transfer-field">
                <span className="transfer-label">Date</span>
                <input
                  className="form-input"
                  type="date"
                  value={transferForm.date}
                  onChange={e => setTransferField('date', e.target.value)}
                />
              </label>

              <label className="transfer-field transfer-field-wide">
                <span className="transfer-label">Note / Memo</span>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. Move to savings, pay down credit card…"
                  value={transferForm.note}
                  onChange={e => setTransferField('note', e.target.value)}
                />
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => setTransferForm(emptyTransfer)}
                className="btn btn-reset"
              >
                Reset
              </button>
              <button type="submit" className="btn btn-primary">
                Transfer Money
              </button>
            </div>
          </>
        )}
      </form>

      <div className="accounts-list">
        {accounts.map(acc => (
          <div key={acc.id} className="account-card">
            <div className="account-info">
              <h3 className="account-name">{acc.name}</h3>
              <p className="account-bank">{acc.bank || '—'}</p>
            </div>
            
            <div className="account-detail">
              <span className="account-detail-label">Balance</span>
              <span className="account-detail-value account-balance">
                {formatCurrency(parseFloat(acc.currentBalance ?? acc.balance) || 0)}
              </span>
            </div>
            
            <div className="account-detail">
              <span className="account-detail-label">Account ID</span>
              <span className="account-detail-value account-id">
                {acc.id}
              </span>
            </div>
            
            <div className="account-actions">
              <button
                onClick={() => openEdit(acc)}
                className="btn btn-edit"
              >
                Edit
              </button>
              <button 
                onClick={() => deleteAccount(acc.id)}
                className="btn btn-delete"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        
        {accounts.length === 0 && (
          <div className="empty-state">
            No accounts yet. Add one using the form above.
          </div>
        )}
      </div>

      {/* Transfer ledger with the full details of every recorded transfer */}
      {transfers.length > 0 && (
        <div className="transfers-section">
          <h2 className="transfers-title">🔁 Transfer Ledger</h2>
          <div className="transfers-table-wrap">
            <table className="transfers-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>From Account</th>
                  <th>To Account</th>
                  <th className="transfers-amount-col">Amount</th>
                  <th>Note / Memo</th>
                  <th>Recorded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transfers.map(t => {
                  // Undo can only safely round-trip when both accounts still exist.
                  const fromAcc = accounts.find(a => a.id === t.fromAccountId);
                  const toAcc = accounts.find(a => a.id === t.toAccountId);
                  const bothExist = !!fromAcc && !!toAcc;
                  const recorded = t.createdAt
                    ? new Date(t.createdAt).toLocaleString([], {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })
                    : '—';
                  const accountCell = (acc) => acc
                    ? (
                      <>
                        <span className="transfer-acc-name">{acc.name}</span>
                        {acc.bank ? <span className="transfer-acc-bank">{acc.bank}</span> : null}
                      </>
                    )
                    : <span className="transfer-stale">Unknown account (removed)</span>;
                  return (
                    <tr key={t.id} className="transfer-card">
                      <td className="transfer-date">{t.date || '—'}</td>
                      <td>{accountCell(fromAcc)}</td>
                      <td>{accountCell(toAcc)}</td>
                      <td className="transfer-amount">{formatCurrency(parseFloat(t.amount) || 0)}</td>
                      <td className="transfer-note">{t.note || '—'}</td>
                      <td className="transfer-recorded">{recorded}</td>
                      <td className="transfer-actions">
                        <button
                          type="button"
                          className="btn btn-delete transfer-undo"
                          onClick={() => undoTransfer(t.id)}
                          disabled={!bothExist}
                          title={bothExist
                            ? 'Undo this transfer and reverse the balances'
                            : 'Undo is unavailable because an account in this transfer was removed'}
                        >
                          Undo
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="total-balance">
        <p className="total-balance-label">Total Balance</p>
        <p className="total-balance-amount">{formatCurrency(total)}</p>
      </div>

      {editing && createPortal(
        <div
          className="account-edit-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}
        >
          <div
            className="account-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-edit-title"
          >
            <div className="account-edit-header">
              <h2 id="account-edit-title" className="account-edit-title">
                ✏️ Edit Account
              </h2>
              <button
                type="button"
                className="account-edit-close"
                onClick={closeEdit}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={saveEdit}>
              <div className="account-edit-body">
                <label className="account-edit-field account-edit-field-full">
                  <span className="account-edit-label">Account Name *</span>
                  <input
                    className="form-input"
                    value={editForm.name}
                    onChange={e => setEditField('name', e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="account-edit-field">
                  <span className="account-edit-label">Bank</span>
                  <input
                    className="form-input"
                    value={editForm.bank}
                    onChange={e => setEditField('bank', e.target.value)}
                  />
                </label>
                <label className="account-edit-field">
                  <span className="account-edit-label">Account Number</span>
                  <input
                    className="form-input"
                    value={editForm.accountNumber}
                    onChange={e => setEditField('accountNumber', e.target.value)}
                  />
                </label>
                <label className="account-edit-field">
                  <span className="account-edit-label">Transit Number</span>
                  <input
                    className="form-input"
                    value={editForm.transitNumber}
                    onChange={e => setEditField('transitNumber', e.target.value)}
                  />
                </label>
                <label className="account-edit-field">
                  <span className="account-edit-label">Branch Number</span>
                  <input
                    className="form-input"
                    value={editForm.branchNumber}
                    onChange={e => setEditField('branchNumber', e.target.value)}
                  />
                </label>
                <label className="account-edit-field">
                  <span className="account-edit-label">Current Balance</span>
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={editForm.currentBalance}
                    onChange={e => setEditField('currentBalance', e.target.value)}
                  />
                </label>
              </div>

              <div className="account-edit-footer">
                <button type="button" onClick={closeEdit} className="btn btn-reset">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AccountsPage;